import * as React from "react";
import { Button, Popover, Typography } from "antd";
import { DialogMessageType } from "@/types/commands";
import { useSlideEditStore, type SlideEditSelection } from "@/state/slideEditStore";
import type { SlideImageResult } from "@/utils/slideUtils";
import { goToSlideByIndexOrId } from "@/utils/slideUtils";
import { InfoCircleOutlined } from "@ant-design/icons";

const PARENT_MSG_SLIDE_IMAGES = "SLIDE_IMAGES";
const PARENT_MSG_SLIDE_IMAGES_ERROR = "SLIDE_IMAGES_ERROR";

const SLIDE_HELP = (
  <p className="m-0 text-sm text-gray-800 leading-relaxed">
    Select the Alphascript created slide you want to edit. Select only 1 slide.
  </p>
);

type SlideImagesFromHostMessage = {
  type?: string;
  slides?: SlideImageResult[];
  selectedSlideIndex?: number;
  selectedSlideId?: string;
  error?: string;
};

function applyHostDefaultSlideSelection(
  list: SlideImageResult[],
  hostIndex: number | undefined,
  hostSlideId: string | undefined
): void {
  const { selectedSlideForEdit: prev, setSelectedSlideForEdit } = useSlideEditStore.getState();
  if (list.length === 0) {
    setSelectedSlideForEdit(null);
    return;
  }
  const stillValid =
    prev != null && list.some((s) => s.slideId === prev.slideId && s.index === prev.index);
  if (stillValid) return;

  let pick: SlideEditSelection | null = null;
  if (typeof hostSlideId === "string" && hostSlideId.length > 0) {
    const row = list.find((s) => s.slideId === hostSlideId);
    if (row) pick = { index: row.index, slideId: row.slideId };
  }
  if (!pick && typeof hostIndex === "number" && Number.isFinite(hostIndex)) {
    const idx = Math.min(Math.max(0, Math.floor(hostIndex)), list.length - 1);
    const row = list[idx];
    if (row) pick = { index: row.index, slideId: row.slideId };
  }
  if (!pick) {
    const row = list[0];
    pick = { index: row.index, slideId: row.slideId };
  }
  setSelectedSlideForEdit(pick);
}

function requestSlidesFromHost() {
  Office.context.ui.messageParent(JSON.stringify({ type: DialogMessageType.REQUEST_SLIDE_IMAGES }));
}

type OfficeUiWithDialogHandlerRemoval = typeof Office.context.ui & {
  removeHandlerAsync?: (
    eventType: Office.EventType,
    options?: { handler?: (args: Office.DialogParentMessageReceivedEventArgs) => void },
    callback?: (result: Office.AsyncResult<void>) => void
  ) => void;
};

const { Title } = Typography;

const SelectSlideStep: React.FC = () => {
  const slideThumbnails = useSlideEditStore((s) => s.slideThumbnails);
  const setSlideThumbnails = useSlideEditStore((s) => s.setSlideThumbnails);
  const [loading, setLoading] = React.useState(() => useSlideEditStore.getState().slideThumbnails === null);
  const [error, setError] = React.useState<string | null>(null);
  const selectedSlideForEdit = useSlideEditStore((s) => s.selectedSlideForEdit);
  const setSelectedSlideForEdit = useSlideEditStore((s) => s.setSelectedSlideForEdit);
  const selectedThumbRef = React.useRef<HTMLButtonElement | null>(null);

  React.useLayoutEffect(() => {
    if (loading || error || !slideThumbnails?.length || !selectedSlideForEdit) {
      return undefined;
    }
    const t = window.setTimeout(() => {
      selectedThumbRef.current?.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "auto",
      });
    }, 0);
    return () => window.clearTimeout(t);
  }, [loading, error, slideThumbnails, selectedSlideForEdit]);

  React.useEffect(() => {
    const handler = (arg: Office.DialogParentMessageReceivedEventArgs) => {
      try {
        const data = JSON.parse(arg.message) as SlideImagesFromHostMessage;
        if (data.type === PARENT_MSG_SLIDE_IMAGES) {
          const list = Array.isArray(data.slides) ? data.slides : [];
          setSlideThumbnails(list);
          applyHostDefaultSlideSelection(list, data.selectedSlideIndex, data.selectedSlideId);
          setLoading(false);
          setError(null);
        } else if (data.type === PARENT_MSG_SLIDE_IMAGES_ERROR) {
          setError(typeof data.error === "string" ? data.error : "Failed to load slides.");
          setLoading(false);
        }
      } catch {
        /* ignore non-JSON messages from parent */
      }
    };

    Office.context.ui.addHandlerAsync(Office.EventType.DialogParentMessageReceived, handler, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        setLoading(false);
        setError(result.error?.message || "Could not listen for slide data from the host.");
        return;
      }
      if (useSlideEditStore.getState().slideThumbnails === null) {
        requestSlidesFromHost();
      } else {
        setLoading(false);
      }
    });

    return () => {
      (Office.context.ui as OfficeUiWithDialogHandlerRemoval).removeHandlerAsync?.(
        Office.EventType.DialogParentMessageReceived,
        { handler }
      );
    };
  }, [setSlideThumbnails]);

  const onThumbClick = async (row: SlideImageResult) => {
    setSelectedSlideForEdit({ index: row.index, slideId: row.slideId });
    try {
      await goToSlideByIndexOrId(row.index, row.slideId);
    } catch {
      /* navigation is best-effort; selection still recorded */
    }
  };

  return (
    <div className="p-4 text-sm text-gray-800">
      {loading && <div className="text-sm text-gray-600">Loading slides…</div>}
      {error && (
        <div className="mb-2 text-sm text-red-700">
          {error}
          <Button
            type="link"
            size="small"
            className="ml-2 h-auto p-0"
            onClick={() => {
              setLoading(true);
              setError(null);
              requestSlidesFromHost();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && slideThumbnails && slideThumbnails.length === 0 && (
        <div className="text-sm text-gray-600">No slides found in this presentation.</div>
      )}

      {!loading && !error && slideThumbnails && slideThumbnails.length > 0 && (
        <div className="">
          <div className="flex flex-wrap items-center gap-1">
            <Title level={3} className="!mb-0 font-bold">
              Select Slide You Want to Edit:
            </Title>

            <Popover
            content={SLIDE_HELP}
            trigger="click"
            overlayStyle={{ maxWidth: 380 }}
          >
            <button
              type="button"
              className="inline-flex items-center justify-center rounded p-0.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
              aria-label="More information about slide frame precedents"
            >
              <InfoCircleOutlined className="text-lg" />
            </button>
          </Popover>
          </div>

          <div
            className="mt-3 w-full max-h-[69vh] overflow-y-auto overscroll-contain pr-1"
            aria-label="Presentation slides"
          >
            <div className="grid w-full grid-cols-2 gap-3 pb-1">
              {slideThumbnails.map((row) => {
                const selected =
                  selectedSlideForEdit != null &&
                  (selectedSlideForEdit.slideId === row.slideId || selectedSlideForEdit.index === row.index);
                return (
                  <button
                    key={row.slideId || String(row.index)}
                    ref={selected ? selectedThumbRef : undefined}
                    type="button"
                    onClick={() => void onThumbClick(row)}
                    className={`w-full overflow-hidden bg-gray-50 text-left shadow-sm transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-1 ${
                      selected ? "border-2 border-[#FF5C35]" : "border-2 border-gray-200"
                    }`}
                    aria-pressed={selected}
                    aria-label={`Slide ${row.index + 1}. Click to select.`}
                  >
                    <div className="aspect-video w-full">
                      <img
                        src={`data:image/png;base64,${row.imageBase64}`}
                        alt=""
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectSlideStep;
